import { useCallback, SyntheticEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Input, Button, IconButton, Helper, TInputSize } from '@treezor/design-system';

import { TCountryCode } from '@/features/user/store/user.model';
import { CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';

interface ITaxResidenceProps {
  taxResidences: ITaxResidenceCreate[];
  defaultCountry?: TCountryCode;
  size?: TInputSize;
  isBusiness?: boolean;
  onUpdate: (value: ITaxResidenceCreate[]) => void;
}

export const defaultTaxResidence = {
  country: null,
  taxPayerId: '',
  liabilityWaiver: true,
  userId: null,
};

export function CreateUserTaxResidences({
  taxResidences,
  defaultCountry,
  onUpdate,
  size = 'm',
  isBusiness,
}: ITaxResidenceProps) {
  const { t } = useTranslation();
  const { countryCodeOptions } = useFieldOptions();
  const { getInputError } = useAddTaxResidences();
  const isCountrySelected = useCallback(
    (country: TCountryCode, currentIndex: number) =>
      taxResidences.some((taxResidence, index) => {
        if (index < currentIndex && taxResidence.country) {
          return taxResidence.country === country;
        }
        return false;
      }),
    [taxResidences],
  );

  const handleChange = useCallback(
    (index: number, key: keyof ITaxResidenceCreate, value: string) => {
      const newTaxResidences = [...taxResidences];
      newTaxResidences[index] = {
        ...newTaxResidences[index],
        [key]: value,
        liabilityWaiver: key === 'taxPayerId' ? !value : newTaxResidences[index].liabilityWaiver,
      };
      onUpdate(newTaxResidences);
    },
    [onUpdate, taxResidences],
  );

  const handleDelete = useCallback(
    (indexToDelete) => {
      const newTaxResidences = taxResidences.filter((_, index) => index !== indexToDelete);
      onUpdate(newTaxResidences);
    },
    [onUpdate, taxResidences],
  );

  useEffect(() => {
    if (!taxResidences) {
      onUpdate([{ ...defaultTaxResidence, country: defaultCountry || null }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  if (!taxResidences) return <></>;

  const taxResidenceMessages = {
    US: t('pages.currentProfiles.profile.taxResidence.form.taxPayerIdHelperUS'),
    IT: t('pages.currentProfiles.profile.taxResidence.form.taxPayerIdHelperItaly'),
  };

  return (
    <>
      {taxResidences.map((taxResidence, index) => {
        const isRequiredTaxResidence = taxResidence.country === 'US' || taxResidence.country === 'IT';

        return (
          <div key={index}>
            <CustomColumns key={`tax-column-${index}`} columns={[5, 5, 1]}>
              <Select
                label={t('pages.currentProfiles.profile.contact.country')}
                options={countryCodeOptions}
                withSearch
                defaultValue={taxResidence.country}
                error={
                  isCountrySelected(taxResidence.country, index)
                    ? t('pages.currentProfiles.profile.taxResidence.form.rules.country')
                    : undefined
                }
                onChange={(value: TCountryCode) => handleChange(index, 'country', value)}
                noOptionsComponent={<CreateUserEmptyComponent />}
                size={size}
                testId={`select-kyc-country-code-${index}`}
              />
              <Input
                type="text"
                value={taxResidence.taxPayerId ?? ''}
                label={t('pages.currentProfiles.profile.taxResidence.taxPayerId')}
                required={isRequiredTaxResidence}
                error={getInputError(taxResidence, isBusiness)}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleChange(index, 'taxPayerId', event.currentTarget.value)
                }
                size={size}
                testId={`select-kyc-tax-payer-id-${index}`}
              />
              <div className="flex items-center justify-center pt-5">
                <IconButton
                  variant="secondary"
                  icon="delete"
                  onClick={() => handleDelete(index)}
                  size={size}
                  testId={`icon-btn-kyc-delete-${index}`}
                />
              </div>
            </CustomColumns>

            <Helper withIcon type={isRequiredTaxResidence ? 'warning' : 'info'} className="pt-3">
              {isRequiredTaxResidence
                ? taxResidenceMessages[taxResidence.country]
                : t('pages.currentProfiles.profile.taxResidence.form.taxPayerInfo')}
            </Helper>
          </div>
        );
      })}
      <div>
        <Button
          variant="ghost"
          icon="add_circle"
          size="s"
          onClick={() => onUpdate([...taxResidences, defaultTaxResidence])}
          testId="btn-kyc-add-tax-residence"
        >
          {t('pages.currentProfiles.profile.taxResidence.form.addItem')}
        </Button>
      </div>
    </>
  );
}
